import { useState } from 'react';
import usePageTitle from '@/hooks/use-page-title';
import { Link } from "react-router-dom";
import { formatDate } from '@/lib/dates';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pencil } from 'lucide-react';
import useIsPWA from '@/hooks/use-is-pwa';
import AprilPicto from '@/components/shared/april-picto';
import {
    PictoEspaceAssure, PictoCall, PictoEmmenagement, PictoFacilitePaiement,
    PictoSatisfactionClient, PictoContrats
} from '@/assets/icons-april-fr/pictos';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { useContracts } from '@/hooks/api/use-contracts';
import { useCurrentUser } from '@/hooks/api/use-user';
import { useUpdateDematStatut } from '@/hooks/api/use-profile';
import { hasOnlyContractApporteur43397 } from '@/lib/contracts';

const MonProfil = () => {
    usePageTitle('Mon profil');
    const isPWA = useIsPWA();
    const user = useCurrentUser();
    const contractsQuery = useContracts();
    const contracts = contractsQuery.data?.data ?? [];
    const postDematStatut = useUpdateDematStatut();

    const titre = user.titre;
    const telephone = user.address_phone;
    const email = user.email;
    const titulaire = user.banque?.titulaire;
    const iban = user.banque?.iban;
    const souscripteur = contracts[0]?.souscripteur;
    const paysNaissance = souscripteur?.pays_naissance;
    const villeNaissance = souscripteur?.ville_naissance;
    const nomNaissance = souscripteur?.nom_naissance;
    const statut_juridique = souscripteur?.statut_juridique;
    const num_siren_siret = souscripteur?.num_siren_siret;
    const beneficiairesEffectifs = user.beneficiaires_effectifs;
    const hasOnlyApporteur43397 = hasOnlyContractApporteur43397(contracts);

    const canModifyRib = contracts.some(contract => contract.situation?.statutCode === '1');

    const [dematStatus, setDematStatus] = useState(user.statut_demat);
    const handleDematChange = () => {
        const newDematStatus = dematStatus === 'T' ? 'F' : 'T';

        postDematStatut.mutate({
            form: { b_stopemail: newDematStatus },
            successFunction: () => {
                setDematStatus(newDematStatus);
            }
        });
    };

    if (contractsQuery.isPending) return <SmallSpinner />;

    return (
        <div className="space-y-6">
            {/* Hero */}
            <div className="relative overflow-hidden rounded-2xl bg-primary p-8 text-white mt-12">
                <div className="absolute -top-1/2 -right-[10%] w-[400px] h-[400px] bg-white/10 rounded-full" />
                <div className="relative z-10">
                    <p className="text-sm mb-1 text-white">Mon profil</p>
                    <h1 className="text-3xl font-semibold">Mon compte</h1>
                </div>
            </div>

            <div data-tutorial="profil-cards" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Informations de contact */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AprilPicto icon={PictoCall} size="md" />
                                Informations de contact
                            </CardTitle>
                            {hasOnlyApporteur43397 && (
                            <Button asChild variant="outline" size="sm" className="cursor-pointer">
                                <Link to="/mon-profil/je-change-mes-informations">
                                    <Pencil className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Modifier</span>
                                </Link>
                            </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid gap-y-5">
                            <div>
                                <p className="text-sm text-muted-foreground mb-0.5">Numéro de téléphone</p>
                                <p className="font-medium">{telephone}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-0.5">Adresse email</p>
                                <p className="font-medium">{email}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Informations personnelles */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                {titre === 'SOC'
                                    ? <AprilPicto icon={PictoEmmenagement} size="md" />
                                    : <AprilPicto icon={PictoEspaceAssure} size="md" />
                                }
                                Informations personnelles
                            </CardTitle>
                            {hasOnlyApporteur43397 && (
                            <Button asChild variant="outline" size="sm" className="cursor-pointer">
                                <Link to="/mon-profil/je-change-mes-informations-de-naissance">
                                    <Pencil className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">Modifier</span>
                                </Link>
                            </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {titre === 'SOC' ? (
                            <div className="grid gap-y-5">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Statut juridique</p>
                                    <p className="font-medium">{statut_juridique ?? 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Numéro SIREN/SIRET</p>
                                    <p className="font-medium">{num_siren_siret ?? 'Non renseigné'}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid gap-y-5">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Lieu de naissance</p>
                                    <p className="font-medium">{villeNaissance ?? 'Non renseigné'} - {paysNaissance ?? 'Non renseigné'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Nom de naissance</p>
                                    <p className="font-medium">{nomNaissance ?? 'Non renseigné'}</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Bénéficiaires effectifs (SOC uniquement) */}
                {titre === 'SOC' && (
                    <Card>
                        <CardHeader className="pb-6 border-b">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AprilPicto icon={PictoEspaceAssure} size="md" />
                                Bénéficiaires effectifs
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            {beneficiairesEffectifs && beneficiairesEffectifs.length > 0 ? (
                                <div className="grid gap-3">
                                    {beneficiairesEffectifs.map((beneficiaire, index) => (
                                        <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                            <div>
                                                <p className="font-medium">
                                                    {beneficiaire.nom} {beneficiaire.prenom}
                                                    {beneficiaire.nom_naissance ? `, né(e) ${beneficiaire.nom_naissance}` : ''}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    Né(e) le {formatDate(beneficiaire.naissance)} à {beneficiaire.ville_naissance}, {beneficiaire.pays_naissance}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-foreground">Vous n'avez pas de bénéficiaire effectif de renseigné.</p>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Informations bancaires */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <AprilPicto icon={PictoFacilitePaiement} size="md" />
                                Informations bancaires
                            </CardTitle>
                            {canModifyRib && (
                                <Button asChild variant="outline" size="sm" className="cursor-pointer">
                                    <Link to="/mon-profil/je-change-mes-informations-bancaires">
                                        <Pencil className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Modifier</span>
                                    </Link>
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {iban ? (
                            <div className="grid gap-y-5">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">Titulaire du compte</p>
                                    <p className="font-medium">{titulaire}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground mb-0.5">IBAN</p>
                                    <p className="font-medium">{iban}</p>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Vous n'avez aucun IBAN d'enregistré.</p>
                        )}
                    </CardContent>
                </Card>

                {/* Paramètres */}
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <AprilPicto icon={PictoEspaceAssure} size="md" />
                            Paramètres
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid gap-3">
                            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg transition-all duration-200 hover:bg-primary/10 hover:translate-x-1">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0">
                                        <AprilPicto icon={PictoEspaceAssure} size="md" />
                                    </div>
                                    <p className="font-medium">Modifier mon mot de passe</p>
                                </div>
                                <Button asChild variant="outline" size="sm" className="cursor-pointer">
                                    <Link to="/mon-profil/je-change-mon-mot-de-passe">
                                        <Pencil className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Modifier</span>
                                    </Link>
                                </Button>
                            </div>
                            {hasOnlyApporteur43397 && (
                            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg transition-all duration-200 hover:bg-primary/10 hover:translate-x-1">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0">
                                        <AprilPicto icon={PictoSatisfactionClient} size="md" />
                                    </div>
                                    <p className="font-medium">Préférences d'abonnement</p>
                                </div>
                                <Button asChild variant="outline" size="sm" className="cursor-pointer">
                                    <Link to="/mon-profil/je-change-mes-abonnements">
                                        <Pencil className="w-3.5 h-3.5" />
                                        <span className="hidden sm:inline">Modifier</span>
                                    </Link>
                                </Button>
                            </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Gestion de la dématérialisation */}
                {hasOnlyApporteur43397 && (
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <AprilPicto icon={PictoContrats} size="md" />
                            Gestion de la dématérialisation
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground mb-6">
                            Pour faciliter les échanges, votre contrat est soumis à la dématérialisation de l'avis d'échéance. Vous pouvez choisir de refuser la dématérialisation ci-dessous :
                        </p>
                        <div className={`${isPWA ? 'flex flex-col items-center gap-3' : 'flex items-center justify-between'} p-4 bg-muted/50 rounded-lg`}>
                            <div className={isPWA ? 'text-center' : ''}>
                                <p className="text-sm text-muted-foreground mb-0.5">Statut de la dématérialisation</p>
                                {dematStatus === 'T'
                                    ? <Badge variant="destructive">Désactivé</Badge>
                                    : <Badge variant="success">Activé</Badge>
                                }
                            </div>
                            <Button
                                variant="outline"
                                size={isPWA ? "xs" : "sm"}
                                onClick={handleDematChange}
                                className={isPWA ? "cursor-pointer text-xs" : "cursor-pointer"}
                            >
                                {dematStatus === 'T' ? 'Activer' : 'Désactiver'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
                )}
            </div>
        </div>
    );
};

export default MonProfil;
